import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, EyeOff, HelpCircle } from 'lucide-react';
import { statistik, type StatZeitraum } from '../api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Was die Messung allein nicht beantwortet.
 *
 * Aus den Zahlen laesst sich ablesen, was besucht wurde. Die Frage, die im
 * Marketing haeufiger gestellt wird, ist die andere: **welche Seite sieht
 * eigentlich niemand?** Dafuer braucht es eine Sollliste — und die kommt aus
 * der Sitemap, die die Webseite ohnehin fuehrt.
 *
 * Die Gegenrichtung faellt dabei kostenlos ab und ist meistens die
 * nuetzlichere: eine Seite, die aufgerufen wird und in keiner Sitemap steht,
 * ist entweder vergessen worden oder haette laengst verschwinden sollen.
 */

function Liste({
  titel,
  hinweis,
  symbol,
  eintraege,
}: {
  titel: string;
  hinweis: string;
  symbol: React.ReactNode;
  eintraege: { pfad: string; zusatz?: string }[];
}) {
  if (eintraege.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-200">
      <div className="border-b border-slate-100 px-3 py-2">
        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          {symbol}
          {titel}
          <span className="text-xs font-normal text-slate-400">({eintraege.length})</span>
        </div>
        <p className="mt-0.5 text-[11px] leading-tight text-slate-500">{hinweis}</p>
      </div>
      <ul className="max-h-64 divide-y divide-slate-100 overflow-auto">
        {eintraege.map((e) => (
          <li key={e.pfad} className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span className="font-mono text-xs text-slate-700">{e.pfad}</span>
            {e.zusatz && <span className="shrink-0 text-[11px] text-slate-400">{e.zusatz}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function StatistikAbgleich({
  instanceId,
  zeitraum,
}: {
  instanceId: string;
  zeitraum: StatZeitraum;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const darfLesen = user?.role === 'ADMIN' || user?.role === 'OPERATIONS';
  const [ergebnis, setErgebnis] = useState('');

  const { data } = useQuery({
    queryKey: ['statistik', instanceId, 'abgleich', zeitraum],
    queryFn: () => statistik.abgleich(instanceId, zeitraum),
    refetchOnWindowFocus: false,
  });

  const lesen = useMutation({
    mutationFn: () => statistik.sitemapLesen(instanceId),
    onSuccess: (r) => {
      setErgebnis(
        r.hinweis ??
          t('webstat.sitemapResult', { count: r.gelesen, added: r.neu, gone: r.entfernt }),
      );
      qc.invalidateQueries({ queryKey: ['statistik', instanceId, 'abgleich'] });
    },
  });

  if (!data) return null;

  const nichts = data.ohneAufrufe.length === 0 && data.ohneSitemap.length === 0;

  return (
    <div className="mt-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-700">{t('webstat.comparison')}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">
            {data.sitemapStand
              ? t('webstat.sitemapState', {
                  when: new Date(data.sitemapStand).toLocaleDateString(),
                })
              : null}
          </span>
          {darfLesen && (
            <button
              type="button"
              onClick={() => lesen.mutate()}
              disabled={lesen.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={12} className={lesen.isPending ? 'animate-spin' : ''} />
              {lesen.isPending ? t('webstat.reading') : t('webstat.readSitemap')}
            </button>
          )}
        </div>
      </div>

      {ergebnis && (
        <div className="mb-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
          {ergebnis}
        </div>
      )}

      {!data.sitemapStand ? (
        <p className="text-sm text-slate-400">{t('webstat.sitemapNever')}</p>
      ) : nichts ? (
        <p className="text-sm text-slate-400">{t('webstat.noComparison')}</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          <Liste
            titel={t('webstat.unvisited')}
            hinweis={t('webstat.unvisitedHint')}
            symbol={<EyeOff size={14} className="text-slate-400" />}
            eintraege={data.ohneAufrufe.map((s) => ({
              pfad: s.pfad,
              zusatz: s.entfernt ? t('webstat.removedFromSitemap') : undefined,
            }))}
          />
          <Liste
            titel={t('webstat.unlisted')}
            hinweis={t('webstat.unlistedHint')}
            symbol={<HelpCircle size={14} className="text-slate-400" />}
            eintraege={data.ohneSitemap.map((s) => ({
              pfad: s.pfad,
              zusatz: String(s.aufrufe),
            }))}
          />
        </div>
      )}
    </div>
  );
}
